import clsx from 'clsx';
import { type PropsWithChildren } from 'react';

import { GlobalFontSizeVariables } from './components/GlobalFontSizeVariables.js';
import { SingleValueDisplay } from './components/SingleValueDisplay.js';
import { DISPLAY_NAME, SINGLE_VALUE_GRID_ARIA_LABEL } from './defaults.js';
import { useLabelCount } from './hooks/use-label-count.js';
import { GlobalFontSizeProvider } from './providers/global-font-size-provider.js';
import { gridRows } from './SingleValueGrid.sty.js';
import { Sparkline } from './slots/Sparkline.js';
import { SingleValueGridToolbarSlot } from './slots/Toolbar.js';
import { Trend } from './slots/Trend.js';
import { Value } from './slots/Value.js';
import type {
  SingleValueGridAccessorsConfig,
  SingleValueGridProps,
} from './types/single-value-grid.js';
import { calculateGridLayout } from './utils/calculate-grid-layout.js';
import { iterateSlots } from './utils/iterate-slots.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { Grid } from '../../layouts/grid/Grid.js';
import { ChartStateHandler } from '../core/components/chart-states/ChartStateHandler.js';
import { SingleValueToolbar } from '../core/components/single-value-renderer/components/SingleValueToolbar.js';
import { useThrottledResizeObserver } from '../core/hooks/useThrottledResizeObserver.js';
import { ColorRule } from '../core/slots/ColorRule.js';

const COMPONENT_NAME = 'SingleValueGrid';

const SingleValueGridRoot = (
  props: PropsWithChildren<SingleValueGridProps>,
) => {
  const {
    loading,
    data,
    children,
    colorAccessor,
    labelAccessor,
    prefixIconAccessor,
    applyThresholdBackground,
    style: consumerStyle,
    className: consumerClassName,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    seriesActions: individualSeriesActions,
    ...gridConfig
  } = props;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(props);

  const labelCount = useLabelCount(data, labelAccessor);

  const { accessors: slotAccessors, ...slotsConfig } = iterateSlots(
    children,
    props,
  );

  const trendCount = slotsConfig.trend !== undefined ? data.length : 0;

  const accessors: SingleValueGridAccessorsConfig = {
    colorAccessor,
    labelAccessor,
    prefixIconAccessor,
    ...slotAccessors,
  };

  const propsConfig = {
    ...gridConfig,
    ...slotsConfig,
    applyThresholdBackground,
  };

  const { height = 0, width = 0, refCallback } = useThrottledResizeObserver();

  const { rowCount, columnCount, lastRowColumnCount, gap } =
    calculateGridLayout(width, height, data.length);

  const gridItems = data.slice(0, data.length - lastRowColumnCount);
  const lastRowItems = data.slice(-lastRowColumnCount);
  const { intents, toolbar } = propsConfig;
  const displayingGridToolbar = !intents.single;

  return (
    <div
      {...behavioralTrackingProps}
      ref={refCallback}
      style={{
        ...consumerStyle,
      }}
      className={clsx(gridRows)}
    >
      <ChartStateHandler
        height={height}
        isLoading={!!loading}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        style={consumerStyle}
        className={consumerClassName}
        empty={false}
        emptyState={undefined}
        errorState={undefined}
      >
        {/* Empty children as the loader overlay will be shown */}
        <GlobalFontSizeProvider
          valueCount={data.length}
          labelCount={labelCount}
          trendCount={trendCount}
        >
          <GlobalFontSizeVariables>
            {displayingGridToolbar ? (
              <SingleValueToolbar
                parentHeight={height}
                intents={displayingGridToolbar ? intents.grid : []}
                placement={toolbar.placement}
                hidden={toolbar.hidden}
              />
            ) : null}

            <Grid
              className={clsx(gridRows, consumerClassName)}
              data-dtrum-mask={dataDtrumMask}
              data-dtrum-allow={dataDtrumAllow}
              data-testid={dataTestId}
              data-dt-component={COMPONENT_NAME}
              gridTemplateRows={`repeat(${rowCount}, 1fr)`}
              gridTemplateColumns={`repeat(${columnCount}, 1fr)`}
              style={{
                ...consumerStyle,
                // Explicitly set this one via the style, as the `gridGap` settable via the
                // props on the `Grid` component needs to be a valid spacing token
                gridGap: gap,
                ...(displayingGridToolbar ? { pointerEvents: 'none' } : {}),
              }}
              // NOSONAR: Complex data visualization treated as single image for screen readers
              role="img"
              aria-label={ariaLabel ?? SINGLE_VALUE_GRID_ARIA_LABEL}
              aria-describedby={ariaDescribedBy}
            >
              {gridItems.map((record, index) => (
                <SingleValueDisplay
                  key={index}
                  record={record}
                  propsConfig={propsConfig}
                  accessors={accessors}
                />
              ))}
              <Flex
                className={gridRows}
                style={{
                  // Explicitly set this one via the style, as the `gridGap` settable via the
                  // props on the `Grid` component needs to be a valid spacing token
                  columnGap: gap,
                }}
                gridColumn="1 / -1"
              >
                {lastRowItems.map((record, index) => (
                  <SingleValueDisplay
                    key={index}
                    record={record}
                    propsConfig={propsConfig}
                    accessors={accessors}
                  />
                ))}
              </Flex>
            </Grid>
          </GlobalFontSizeVariables>
        </GlobalFontSizeProvider>
      </ChartStateHandler>
    </div>
  );
};

SingleValueGridRoot['displayName'] = DISPLAY_NAME;

/**
 * The SingleValueGrid component
 * @public
 */
export const SingleValueGrid = Object.assign(SingleValueGridRoot, {
  ColorRule,
  Intent,
  Sparkline,
  Toolbar: SingleValueGridToolbarSlot,
  Trend,
  Value,
});
